import React from 'react';
import { CustomInput, Form, FormGroup, Input, Label } from 'reactstrap';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { auth } from '../../Api';
import TimeZoneField from '../../components/people/timeZoneField';
import LocalPhoneField from '../../components/people/localPhoneField';
import { CountryFlagSelectOption, CountryFlagValueContainer } from '../companies/company-edit';
import { dialCodeFor } from '../../utils/phone';
import { formatServerMessage } from '../../utils/serverErrors';
import { signupProblem, signupRequest, offersPrivate, termsFor, ACCOUNT_CHOICES, SECURITY_COMPANY, MYSELF } from '../../utils/signup';
import SiteLayout from '../../components/site/SiteLayout';
import { LOGIN_PATH } from '../../components/site/siteLinks';

// The country's zones for the time zone field, from the public sign-up route (no admin token)
const zonesOf = {};
function loadSignupZones(country) {
	const code = country ? String(country).trim().toUpperCase() : '';
	if (code === '') {
		return Promise.resolve([]);
	}
	if (!zonesOf[code]) {
		zonesOf[code] = auth.getSignupZones({ country: code })
			.then((res) => (res && res.data && Array.isArray(res.data.zones) ? res.data.zones : []))
			.catch(() => {
				delete zonesOf[code];
				return [];
			});
	}
	return zonesOf[code];
}

const countryStyles = {
	valueContainer: (base) => ({ ...base, height: '35px' })
};

/**
 * The public sign-up page, "Start a free trial" (/signup, linked from the sign-in page; laravel SignUpController).
 * The company admin's account is made as the super admin's company form makes it, with a free trial (at most 2 guards).
 * The new admin confirms their email address with the link emailed to them before signing in. While sign-up is not
 * open (SIGNUP_ENABLED, or no Terms and Conditions published) the page says so. The hidden "website" field is for
 * bots: people never see it. In the Mobiliis design, inside the main website's header and footer (components/site/).
 * First a choice, "A security company" or "A private individual with your own guards" (only while private Terms and Conditions are
 * published; otherwise the company form at once). Myself: no company name; the account carries the person's name.
 */
class SignUp extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			info: null,
			loadError: false,
			// SECURITY_COMPANY or MYSELF; null until chosen (only asked when both are offered)
			accountType: null,
			companyName: '',
			country: null,
			timezone: null,
			// Whether the chosen country has several time zones, so one must be chosen
			zoneNeeded: false,
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			password: '',
			passwordAgain: '',
			agreed: false,
			website: '',
			problem: null,
			busy: false,
			done: null
		};
		this.countries = countryList().getData();
	}

	componentDidMount() {
		this.load();
	}

	load() {
		auth.getSignupInfo()
			.then((res) => {
				if (res && res.data && res.data.code == 200) {
					this.setState({ info: res.data, loadError: false });
				} else {
					this.setState({ loadError: true });
				}
			})
			.catch(() => this.setState({ loadError: true }));
	}

	set(field) {
		return (event) => this.setState({ [field]: event.target.value, problem: null });
	}

	// The account type chosen: the only one when Myself is not offered
	chosenType() {
		return offersPrivate(this.state.info) ? this.state.accountType : SECURITY_COMPANY;
	}

	submit(event) {
		if (event) {
			event.preventDefault();
		}
		const { info, country } = this.state;
		const accountType = this.chosenType();
		const form = { ...this.state, accountType, country: country ? country.value : null };
		const problem = signupProblem(form);
		if (problem) {
			this.setState({ problem });
			return;
		}
		const terms = termsFor(info, accountType);
		this.setState({ busy: true, problem: null });
		auth.signUp(signupRequest(form, terms ? terms.id : null, this.state.website))
			.then((res) => {
				const data = res && res.data;
				if (data && data.code == 201) {
					this.setState({ busy: false, done: formatServerMessage(data.message, 'Thank you for signing up. Please check your email.') });
					return;
				}
				// The Terms and Conditions changed while the page was open: show the new ones, the box unticked
				if (data && data.terms) {
					this.setState({ info: { ...info, [accountType === MYSELF ? 'private_terms' : 'terms']: data.terms }, agreed: false });
				}
				this.setState({ busy: false, problem: formatServerMessage(data ? data.message : null, 'Sign-up did not work. Please try again.') });
			})
			.catch(() => this.setState({ busy: false, problem: 'Could not reach the server. Please try again.' }));
	}

	// "Which of the following best describes you?": only while both are offered
	renderChoice() {
		const chosen = this.state.accountType;
		return (
			<FormGroup tag="fieldset" className="mb-3">
				<legend className="mob-section-title">Which of the following best describes you?</legend>
				{ACCOUNT_CHOICES.map((choice) => (
					<CustomInput key={choice.value} type="radio" id={'signup-type-' + choice.value} name="signup-type" label={choice.label}
						checked={chosen === choice.value} onChange={() => this.setState({ accountType: choice.value, agreed: false, problem: null })} />
				))}
			</FormGroup>
		);
	}

	renderForm(info) {
		const { companyName, country, timezone, firstName, lastName, email, phone, password, passwordAgain, agreed, website, problem, busy } = this.state;
		const choosing = offersPrivate(info);
		const accountType = this.chosenType();
		if (!accountType) {
			return (
				<Form onSubmit={(e) => e.preventDefault()} noValidate className="mob-card">
					{this.renderChoice()}
					<p className="text-center mt-3 mb-0">Already have an account? <a href={LOGIN_PATH}>Log in</a></p>
				</Form>
			);
		}
		const isPrivate = accountType === MYSELF;
		const terms = termsFor(info, accountType);
		const countryAndPhone = (
			<React.Fragment>
				<FormGroup>
					<Label for="signup-country">Country</Label>
					<Select inputId="signup-country" styles={countryStyles} options={this.countries} value={country}
						components={{ Option: CountryFlagSelectOption, ValueContainer: CountryFlagValueContainer }}
						onChange={(value) => this.setState({ country: value, timezone: null, zoneNeeded: false, problem: null })} />
				</FormGroup>
				{/* Shown only when the country has several time zones; otherwise set from the country without a word */}
				<TimeZoneField country={country ? country.value : null} value={timezone} loadZones={loadSignupZones} onlyWhenChoice
					onZones={(zones) => this.setState({ zoneNeeded: zones.length > 1 })}
					onChange={(zone) => this.setState({ timezone: zone, problem: null })} />
				<FormGroup>
					<Label>Phone</Label>
					<LocalPhoneField dialCode={dialCodeFor(country ? country.value : null)} value={phone}
						onChange={(value) => this.setState({ phone: value, problem: null })} required />
					{isPrivate && <small>You also sign in to the Mobiliis app with it, to see your guards and get their missed alerts.</small>}
				</FormGroup>
			</React.Fragment>
		);
		return (
			<Form onSubmit={(e) => this.submit(e)} noValidate className="mob-card">
				{choosing && this.renderChoice()}
				{!isPrivate && <h2 className="mob-section-title">Company</h2>}
				{!isPrivate && <FormGroup>
					<Label for="signup-company">Company name</Label>
					<Input id="signup-company" value={companyName} onChange={this.set('companyName')} autoComplete="organization" />
				</FormGroup>}
				{!isPrivate && countryAndPhone}

				<h2 className="mob-section-title">Your details</h2>
				<div className="mob-two">
					<FormGroup>
						<Label for="signup-first">First name</Label>
						<Input id="signup-first" value={firstName} onChange={this.set('firstName')} autoComplete="given-name" />
					</FormGroup>
					<FormGroup>
						<Label for="signup-last">Last name</Label>
						<Input id="signup-last" value={lastName} onChange={this.set('lastName')} autoComplete="family-name" />
					</FormGroup>
				</div>
				{isPrivate && countryAndPhone}
				<FormGroup>
					<Label for="signup-email">Email</Label>
					<Input id="signup-email" type="email" value={email} onChange={this.set('email')} autoComplete="email" />
					<small>You sign in with it, and we send the link to confirm it there.</small>
				</FormGroup>

				<h2 className="mob-section-title">Password</h2>
				<div className="mob-two">
					<FormGroup>
						<Label for="signup-password">Password</Label>
						<Input id="signup-password" type="password" value={password} onChange={this.set('password')} autoComplete="new-password"
							placeholder="At least 6 characters" />
					</FormGroup>
					<FormGroup>
						<Label for="signup-password-again">Password again</Label>
						<Input id="signup-password-again" type="password" value={passwordAgain} onChange={this.set('passwordAgain')}
							autoComplete="new-password" />
					</FormGroup>
				</div>

				{/* For bots only: people never see this field, and a sign-up with it filled in is refused */}
				<div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
					<label htmlFor="signup-website">Website</label>
					<input id="signup-website" name="website" type="text" tabIndex="-1" autoComplete="off" value={website}
						onChange={(e) => this.setState({ website: e.target.value })} />
				</div>
				<FormGroup className="mt-3">
					<CustomInput type="checkbox" id="signup-terms" checked={agreed} onChange={(e) => this.setState({ agreed: e.target.checked, problem: null })}
						label={<span>I agree to the <a href={terms ? terms.url : '#'} target="_blank" rel="noopener noreferrer">Terms and Conditions</a></span>} />
				</FormGroup>
				{problem && <div className="mob-message mob-message-error" role="alert">{problem}</div>}
				<button type="submit" className="mob-btn mob-btn-primary mob-btn-block" disabled={busy || !agreed}>
					{busy ? 'Starting your free trial...' : 'Start my free trial'}
				</button>
				<p className="text-center mt-3 mb-0">Already have an account? <a href={LOGIN_PATH}>Log in</a></p>
			</Form>
		);
	}

	// Beside the form (below it on phones): what the trial gives, the real rules (App/Trial.php)
	renderTrialPanel(info) {
		return (
			<aside className="mob-trial-panel">
				<h2 className="mob-section-title">Your free trial</h2>
				<ul>
					<li>1 month free</li>
					<li>Up to {info.max_guards} guards during the trial</li>
					<li>No card needed to start</li>
					<li>Then {info.rate_usd ? 'USD ' + info.rate_usd : 'a small price'} per duty start, invoiced monthly</li>
				</ul>
			</aside>
		);
	}

	render() {
		const { info, loadError, done } = this.state;
		let body;
		if (done) {
			body = <div className="mob-card mob-narrow"><div className="mob-message mob-message-success mb-0" role="status">{done}</div></div>;
		} else if (loadError) {
			body = (
				<div className="mob-card mob-narrow">
					<div className="mob-message mob-message-error mb-0" role="alert">
						The sign-up page could not be loaded. Please try again.{' '}
						<button type="button" className="mob-link-button" onClick={() => this.load()}>Try again</button>
					</div>
				</div>
			);
		} else if (!info) {
			body = <p className="mob-narrow">Loading...</p>;
		} else if (!info.open || !info.terms) {
			body = (
				<div className="mob-card mob-narrow">
					<div className="mob-message mob-message-info mb-0">{info.message || 'Sign-up is not open yet.'}</div>
					<p className="mt-3 mb-0">Already have an account? <a href={LOGIN_PATH}>Log in</a></p>
				</div>
			);
		} else {
			body = (
				<div className="mob-signup">
					{this.renderForm(info)}
					{this.renderTrialPanel(info)}
				</div>
			);
		}
		const form = !!(info && info.open && info.terms && !done && !loadError);
		const accountType = info ? this.chosenType() : SECURITY_COMPANY;
		const lede = accountType === MYSELF ? 'Set up Mobiliis for yourself and your own guards in a few minutes. No card needed.'
			: accountType === SECURITY_COMPANY ? 'Set up Mobiliis for your security company in a few minutes. No card needed.'
			: 'Set up Mobiliis in a few minutes. No card needed.';
		return (
			<SiteLayout page="signup" signupInfo={info}>
				<div className={'mob-heading' + (form ? '' : ' mob-narrow')}>
					<h1 className="mob-title">Start your free trial</h1>
					<p className="mob-lede">{lede}</p>
				</div>
				{body}
			</SiteLayout>
		);
	}
}

export default SignUp;
